import React from "react";
import { Card, CardBody, Row, Col } from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";

const parseSaved = (data) => {
  try { return JSON.parse(data || "[]"); } catch { return []; }
};

const PRESET_CATEGORIES = [
  "documents", "electronics", "clothing", "food",
  "medicines", "cosmetics", "gifts", "books",
  "toys", "accessories", "sports", "homeware",
];

const PRESET_RESTRICTIONS = [
  "weapons", "drugs", "liquids", "perishables",
  "animals", "currency", "jewellery", "hazmat",
];

const TravelerDetails = ({ travelerDetails, offerDetails, fallbackPickup, expectedArrivalAt, expectedArrivalTimezone, orderDetails }) => {
  const { t } = useTranslation();

  if (!travelerDetails && !orderDetails) return null;

  const baggage = travelerDetails?.baggageDetails || {};
  const document = travelerDetails?.documentDetails || {};
  const pickup = travelerDetails?.pickUpDetails || fallbackPickup || {};

  const categoriesWillCarry = parseSaved(baggage.categoriesWillCarry);
  const restrictionsWillNotCarry = parseSaved(baggage.restrictionsWillNotCarry);

  const rawTravelTime = document.travelTime || orderDetails?.departureAt || orderDetails?.acceptedOffer?.departureAt || orderDetails?.acceptedOffer?.offerDetails?.departureTime || orderDetails?.traveler_listing_id?.documentDetails?.travelTime || orderDetails?.travelTime;
  const formattedTime = rawTravelTime ? helpers.formatTime(rawTravelTime) : null;
  const travelTimeDisplay = formattedTime && formattedTime !== "—" ? formattedTime : null;
  const departureTz =
    document.departureTimezone ||
    document.timezone ||
    orderDetails?.departureTimezone ||
    orderDetails?.traveler_listing_id?.departureTimezone ||
    orderDetails?.sender_listing_id?.departureTimezone ||
    helpers.deriveTimezone(travelerDetails?.fromPlace || orderDetails?.sender_listing_id?.fromPlace);
  const departureTzDisplay = helpers.formatTimezone(departureTz);
  const hasDocumentDetails = !!(document.travelType || document.pnrNumber || document.travelTime || travelTimeDisplay || expectedArrivalAt || departureTzDisplay);

  return (
    <>
      <h3 className="mb-3 font-weight-bold heading-lg">
        {t("page.orders.traveler", "Traveler")}
      </h3>

      <Row className="gx-3">
        <Col md={hasDocumentDetails ? "6" : "12"} className="d-flex flex-column">
          <Card className="card-profile shadow border-0 mb-3 flex-grow-1">
            <CardBody className="py-2 px-3 h-100">
              <h4 className="heading-md text-muted mb-2">
                {t("page.orders.baggageDetails.title", "Baggage Details")}
              </h4>

              <p className="heading-sm mb-0">
                <strong>{t("page.orders.baggageDetails.weight", "Weight")}:</strong>{" "}
                {baggage.weight || offerDetails?.weight || t("page.orders.packageDetails.noItems", "N/A")}
              </p>
              <p className="heading-sm mb-1">
                <strong>{t("page.orders.baggageDetails.size", "Size")}:</strong>{" "}
                {baggage.size || offerDetails?.size || t("page.orders.packageDetails.noItems", "N/A")}
              </p>

              {baggage.itemCanBeTaken && (
                <p className="heading-sm mb-0 mt-1">
                  <strong>{t("page.orders.baggageDetails.itemsAllowed", "Items Allowed")}:</strong>{" "}
                  {baggage.itemCanBeTaken}
                </p>
              )}

              {categoriesWillCarry.length > 0 && (
                <div className="mt-2 mb-2">
                  <p className="heading-sm mb-1">
                    <strong>{t("page.create-listing.baggageDetails.categoriesTitle", "What I Can Carry")}:</strong>
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {categoriesWillCarry.map((cat) => (
                      <span
                        key={cat}
                        style={{
                          background: "#EEF4FB",
                          color: "#185FA5",
                          border: "1px solid #185FA5",
                          borderRadius: "6px",
                          fontWeight: 500,
                          fontSize: "0.72rem",
                          padding: "3px 10px",
                          display: "inline-block",
                        }}
                      >
                        {PRESET_CATEGORIES.includes(String(cat).toLowerCase())
                          ? t(`page.create-listing.baggageDetails.categories.${String(cat).toLowerCase()}`)
                          : cat}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {restrictionsWillNotCarry.length > 0 && (
                <div className="mt-2 mb-2">
                  <p className="heading-sm mb-1">
                    <strong>{t("page.create-listing.baggageDetails.restrictionsTitle", "What I Will NOT Carry")}:</strong>
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {restrictionsWillNotCarry.map((r) => (
                      <span
                        key={r}
                        style={{
                          background: "#fff5f5",
                          color: "#dc3545",
                          border: "1px solid #f5c6cb",
                          borderRadius: "999px",
                          fontWeight: 400,
                          fontSize: "0.72rem",
                          padding: "3px 10px",
                          display: "inline-block",
                        }}
                      >
                        ✕ {PRESET_RESTRICTIONS.includes(String(r).toLowerCase())
                          ? t(`page.create-listing.baggageDetails.restrictions.${String(r).toLowerCase()}`)
                          : r}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          <Card className="card-profile shadow border-0 mb-3 flex-grow-1">
            <CardBody className="py-2 px-3 h-100">
              <h4 className="heading-md text-muted mb-2">
                {t("page.create-listing.baggageDetails.handoverPreference", "Handover Preference")}
              </h4>

              <p className="heading-sm mb-0">
                <strong>{t("page.create-listing.baggageDetails.handoverPreference", "Preference")}:</strong>{" "}
                {pickup.handoverPreference || t("page.orders.packageDetails.noItems", "N/A")}
              </p>

              {pickup.meetingPointHint && (
                <p className="heading-sm mb-0 mt-1">
                  <strong>{t("page.create-listing.baggageDetails.meetingPointHint", "Meeting Point")}:</strong>{" "}
                  {pickup.meetingPointHint}
                </p>
              )}
            </CardBody>
          </Card>
        </Col>

        {hasDocumentDetails && (
          <Col md="6" className="d-flex flex-column">
            <Card className="card-profile shadow border-0 mb-3 flex-grow-1">
              <CardBody className="py-2 px-3 h-100">
                <h4 className="heading-md text-muted mb-2">
                  {t("page.orders.documentDetails.title", "Document Details")}
                </h4>

                <p className="heading-sm mb-0">
                  <strong>{t("page.orders.documentDetails.travelType", "Travel Type")}:</strong>{" "}
                  {document.travelType || t("page.orders.packageDetails.noItems", "N/A")}
                </p>
                <p className="heading-sm mb-0">
                  <strong>{t("page.orders.documentDetails.pnr", "PNR")}:</strong>{" "}
                  {document.pnrNumber || t("page.orders.packageDetails.noItems", "N/A")}
                </p>
                <p className="heading-sm mb-0">
                  <strong>{t("page.orders.documentDetails.travelTime", "Departure Time")}:</strong>{" "}
                  {travelTimeDisplay || t("page.orders.packageDetails.noItems", "N/A")}
                </p>
                <p className="heading-sm mb-0">
                  <strong>{t("page.create-listing.documentDetails.timezone", "Time Zone")}:</strong>{" "}
                  {departureTzDisplay || t("page.orders.packageDetails.noItems", "N/A")}
                </p>
                {expectedArrivalAt && (
                  <p className="heading-sm mb-0 mt-1">
                    <strong>{t("page.orders.expectedArrival", "Expected Arrival")}:</strong>{" "}
                    <span>
                      {helpers.formatExpectedArrival(expectedArrivalAt, expectedArrivalTimezone)}
                    </span>
                  </p>
                )}
                {orderDetails?.pickupConfirmedAt && (
                  <p className="heading-sm mb-0 mt-1">
                    <strong>{t("page.orders.pickupConfirmedAt", "Pickup Confirmed At")}:</strong>{" "}
                    <span>
                      {helpers.formatExpectedArrival(
                        orderDetails.pickupConfirmedAt,
                        orderDetails?.departureTimezone ||
                        orderDetails?.acceptedOffer?.departureTimezone ||
                        orderDetails?.traveler_listing_id?.departureTimezone ||
                        orderDetails?.sender_listing_id?.departureTimezone ||
                        helpers.deriveTimezone(travelerDetails?.fromPlace || orderDetails?.sender_listing_id?.fromPlace)
                      )}
                    </span>
                  </p>
                )}
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
};

export default TravelerDetails;
